import React from 'react';

import {
  Button,
  HStack,
  Heading,
  Icon,
  Link as ChakraLink,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import {
  House,
  Mail,
  MessageCircleMore,
  RefreshCw,
  Search,
  type LucideIcon,
} from 'lucide-react';

import type { GlobalErrorFallbackProps } from 'components/GlobalErrorBoundary';
import {
  SeparatedContainer,
  SeparatedContent,
  SeparatedHeader,
  Title,
} from 'components/PageContainer/styles';

import { appEnvironment, isProduction } from 'shared/env';
import { SUPPORT_CONTACTS, type SupportContactType } from 'shared/support';

import {
  Container,
  ContentPanel,
  DetailBlock,
  DetailsPanel,
  ErrorIcon,
  IllustrationWrapper,
  MetadataGrid,
  SupportCard,
  SupportGrid,
} from './styles';

export type UnexpectedErrorPageProps = GlobalErrorFallbackProps;

type DetailSectionProps = {
  title: string;
  content: string;
};

const DetailSection: React.FC<DetailSectionProps> = ({ title, content }) => {
  return (
    <Stack spacing={2} width="100%">
      <Text fontSize="sm" fontWeight="bold" color="gray.200">
        {title}
      </Text>
      <DetailBlock>{content}</DetailBlock>
    </Stack>
  );
};

const formatQueryParams = (searchParams: URLSearchParams): string => {
  const entries = Array.from(searchParams.entries());

  if (entries.length === 0) {
    return 'Nenhum query param presente na URL.';
  }

  return JSON.stringify(Object.fromEntries(entries), null, 2);
};

const getSupportContactTitle = (contactId: SupportContactType): string => {
  return contactId === 'whatsapp' ? 'WhatsApp' : 'E-mail';
};

const getSupportContactIcon = (contactId: SupportContactType): LucideIcon => {
  return contactId === 'whatsapp' ? MessageCircleMore : Mail;
};

const UnexpectedErrorPage: React.FC<UnexpectedErrorPageProps> = ({
  error,
  errorInfo,
  resetErrorBoundary,
}) => {
  const showTechnicalDetails = !isProduction();
  const currentUrl = window.location.href;
  const searchParams = new URLSearchParams(window.location.search);
  const hash = window.location.hash || 'Nenhum fragmento na URL.';
  const timestamp = new Date().toISOString();

  const handleGoHome = (): void => {
    resetErrorBoundary();
    window.location.assign('/');
  };

  const handleReload = (): void => {
    resetErrorBoundary();
    window.location.reload();
  };

  return (
    <SeparatedContainer>
      <SeparatedHeader
        flexDirection="column"
        justifyContent="center"
        textAlign="center"
      >
        <VStack spacing={2} width="100%">
          <Title>Oops, um erro inesperado aconteceu!</Title>
        </VStack>
      </SeparatedHeader>

      <SeparatedContent transparent>
        <Container>
          <ContentPanel>
            <IllustrationWrapper>
              <ErrorIcon aria-hidden="true" />
            </IllustrationWrapper>

            <VStack spacing={3} textAlign="center">
              <Heading as="h2" size="md">
                Não foi possível concluir esta operação agora, mas isso não é
                culpa sua.
              </Heading>
              <Text color="gray.600" maxW="2xl">
                Você pode voltar ao início ou recarregar a página para tentar
                novamente.
              </Text>
            </VStack>

            <HStack
              spacing={3}
              justifyContent="center"
              flexWrap="wrap"
              width="100%"
            >
              <Button
                size="md"
                leftIcon={<Icon as={House} boxSize={4} />}
                colorScheme="brand"
                onClick={handleGoHome}
              >
                Voltar para o início
              </Button>
              <Button
                size="md"
                leftIcon={<Icon as={RefreshCw} boxSize={4} />}
                variant="outline"
                onClick={handleReload}
              >
                Recarregar a página
              </Button>
            </HStack>

            <VStack spacing={3} width="100%">
              <Text
                color="gray.500"
                fontSize="sm"
                maxW="2xl"
                textAlign="center"
              >
                Se o problema continuar, você também pode falar com nosso time
                de suporte pelos canais abaixo.
              </Text>

              <SupportGrid>
                {SUPPORT_CONTACTS.map((contact) => (
                  <SupportCard key={contact.id}>
                    <HStack spacing={3} alignItems="center">
                      <Icon
                        as={getSupportContactIcon(contact.id)}
                        boxSize={5}
                        color="brand.600"
                      />

                      <VStack align="start" spacing={0}>
                        <Text fontSize="xs" color="gray.500">
                          {getSupportContactTitle(contact.id)}
                        </Text>
                        <ChakraLink
                          href={contact.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          color="brand.600"
                          fontWeight="semibold"
                        >
                          {contact.label}
                        </ChakraLink>
                      </VStack>
                    </HStack>
                  </SupportCard>
                ))}
              </SupportGrid>
            </VStack>
          </ContentPanel>

          {showTechnicalDetails && (
            <DetailsPanel>
              <VStack align="stretch" spacing={4}>
                <VStack align="stretch" spacing={1}>
                  <Heading
                    as="h3"
                    size="sm"
                    display="flex"
                    alignItems="center"
                    gap={2}
                  >
                    <Icon as={Search} boxSize={4} />
                    Detalhes técnicos
                  </Heading>
                  <Text fontSize="sm" color="gray.300">
                    Ambiente: {appEnvironment()}
                  </Text>
                  <Text fontSize="sm" color="gray.300">
                    Caminho: {window.location.pathname}
                  </Text>
                </VStack>

                <MetadataGrid>
                  <DetailSection title="URL completa" content={currentUrl} />
                  <DetailSection
                    title="Query params"
                    content={formatQueryParams(searchParams)}
                  />
                  <DetailSection title="Hash da URL" content={hash} />
                  <DetailSection
                    title="Timestamp do erro"
                    content={timestamp}
                  />
                </MetadataGrid>

                <DetailSection
                  title="Mensagem do erro"
                  content={`${error.name}: ${error.message}`}
                />

                {error.stack && (
                  <DetailSection title="Stack trace" content={error.stack} />
                )}

                {errorInfo?.componentStack && (
                  <DetailSection
                    title="Component stack"
                    content={errorInfo.componentStack}
                  />
                )}
              </VStack>
            </DetailsPanel>
          )}
        </Container>
      </SeparatedContent>
    </SeparatedContainer>
  );
};

export default UnexpectedErrorPage;
